import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { Container, useResponsive } from '@garnish/core';
import { ScrollViewWithHeaderTitle, useNavbarContainerHeight } from '@garnish/navigation';

import { AppFooter } from '../../../../components';
import { useIsFoldableDevice } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardConfirmationContainer = (props: Props) => {
  const { isModal, children } = props;

  const { currentBreakpoint, match, minWidth } = useResponsive();

  const { paddingBottomStyle: navbarPaddingBottomStyle, paddingTopStyle: navbarPaddingTopStyle } =
    useNavbarContainerHeight();
  const { top: safeAreaTop } = useSafeAreaInsets();
  const isFoldableDevice = useIsFoldableDevice();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldApplyNavbarBottomPadding = currentBreakpoint.isXS;
  const shouldApplyNavbarTopPadding = minWidth.isSM;
  const shouldApplyTopSafeArea = currentBreakpoint.isXS && isFoldableDevice;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = match([styles.containerXS, styles.containerSM]);
  const topSafeAreaStyles = useStyle(
    () => ({ paddingTop: Math.max(safeAreaTop + theme.spacing['4'], theme.spacing['6']) }),
    [safeAreaTop],
  );
  const contentContainerStyles = [
    styles.contentContainerStyle,
    shouldApplyNavbarBottomPadding ? navbarPaddingBottomStyle : undefined,
    shouldApplyNavbarTopPadding ? navbarPaddingTopStyle : undefined,
    shouldApplyTopSafeArea ? topSafeAreaStyles : undefined,
  ];
  const modalContainerStyles = [
    styles.containerModal,
    currentBreakpoint.isXS ? styles.containerModalXS : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────────────

  if (isModal) {
    return <View style={modalContainerStyles}>{children}</View>;
  }

  return (
    <ScrollViewWithHeaderTitle contentContainerStyle={contentContainerStyles}>
      <Container style={containerStyles}>{children}</Container>

      {minWidth.isSM ? <AppFooter /> : null}
    </ScrollViewWithHeaderTitle>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainerStyle: {
    minHeight: '100%',
  },
  containerXS: {
    paddingBottom: theme.spacing['4'],
  },
  containerSM: {
    alignItems: 'center',
    paddingVertical: theme.spacing['12'],
  },
  containerModal: {
    alignItems: 'center',
    paddingHorizontal: theme.spacing['6'],
    paddingTop: theme.spacing['4'],
    paddingBottom: theme.spacing['6'],
  },
  containerModalXS: {
    paddingTop: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  isModal: boolean;
}>;
